import { Injectable } from '@angular/core';

interface RegisteredComponent {
  element: HTMLElement;
  callback: (files: File[]) => void;
}

@Injectable({
  providedIn: 'root',
})
export class PasteListenerService {
  private registeredComponents: RegisteredComponent[] = [];
  private isListenerAttached = false;

  constructor() {
    this.attachGlobalPasteListener();
  }

  /**
   * Register a component to receive paste events.
   * Only the component that is currently focused/hovered will receive the event.
   */
  registerComponent(
    element: HTMLElement,
    callback: (files: File[]) => void,
  ): void {
    this.registeredComponents.push({ element, callback });
  }

  /**
   * Unregister a component from receiving paste events.
   */
  unregisterComponent(element: HTMLElement): void {
    this.registeredComponents = this.registeredComponents.filter(
      (comp) => comp.element !== element,
    );
  }

  /**
   * Attach global paste listener to the document.
   */
  private attachGlobalPasteListener(): void {
    if (this.isListenerAttached) return;

    document.addEventListener('paste', (event: ClipboardEvent) => {
      this.handleGlobalPaste(event);
    });

    this.isListenerAttached = true;
  }

  /**
   * Handle the global paste event.
   * Determine which component should receive the pasted files.
   */
  private handleGlobalPaste(event: ClipboardEvent): void {
    const clipboardItems = event.clipboardData?.items;
    if (!clipboardItems) return;

    const pastedFiles: File[] = [];

    for (let i = 0; i < clipboardItems.length; i++) {
      const item = clipboardItems[i];

      // Check if the clipboard item is a file and if it's an image
      if (item.kind === 'file' && item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) {
          pastedFiles.push(file);
        }
      }
    }

    if (pastedFiles.length === 0) return;

    // Find the currently focused/active upload component
    const activeComponent = this.findActiveComponent();

    if (activeComponent) {
      // Prevent default paste behavior only if a component is active
      event.preventDefault();
      activeComponent.callback(pastedFiles);
    }
  }

  /**
   * Find the currently active (focused or hovered) upload component.
   * Components hidden via [hidden]/display:none (e.g. inactive wizard steps)
   * are excluded so paste doesn't leak into steps where the upload isn't shown.
   */
  private findActiveComponent(): RegisteredComponent | null {
    const visibleComponents = this.registeredComponents.filter((comp) =>
      this.isElementVisible(comp.element),
    );

    if (visibleComponents.length === 0) {
      return null;
    }

    // Priority 1: Check if any visible component has focus
    const focusedComponent = visibleComponents.find((comp) => {
      return comp.element.contains(document.activeElement);
    });

    if (focusedComponent) {
      return focusedComponent;
    }

    // Priority 2: Check if any visible component is being hovered
    const hoveredComponent = visibleComponents.find((comp) => {
      return comp.element.matches(':hover');
    });

    if (hoveredComponent) {
      return hoveredComponent;
    }

    // Priority 3: Return the first visible registered component
    return visibleComponents[0];
  }

  /**
   * Checks whether the component's element is actually rendered/visible,
   * i.e. not hidden by [hidden] or display:none on itself or an ancestor.
   */
  private isElementVisible(element: HTMLElement): boolean {
    if (!element.isConnected || element.offsetParent === null) {
      return false;
    }

    const rect = element.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  }
}
